import { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { 
    Loader2, 
    Mail, 
    AlertCircle,
    CheckCircle2,
    UserPlus,
    Code2,
    Shield,
    Search,
    Building2,
    XCircle
} from 'lucide-react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useEnhancedToast } from "@/components/ui/enhanced-toast";
import { verifyUser, inviteDeveloper, inviteManager } from "@/api/root-admin/root-admin.api";
import type { VerifyUserResponse } from "@/api/root-admin/root-admin.types";
import { cn } from "@/lib/utils";

const emailSchema = z.object({
    email: z.string().email("Please enter a valid email address"),
});

type EmailFormValues = z.infer<typeof emailSchema>;

interface InviteMemberDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onSuccess?: () => void;
}

type VerificationState = 'idle' | 'verifying' | 'verified' | 'error';

export function InviteMemberDialog({
    open,
    onOpenChange,
    onSuccess,
}: InviteMemberDialogProps) {
    const { error, success } = useEnhancedToast();
    const [verificationState, setVerificationState] = useState<VerificationState>('idle');
    const [verificationResult, setVerificationResult] = useState<VerifyUserResponse | null>(null);
    const [selectedRole, setSelectedRole] = useState<'developer' | 'manager'>('developer');
    const [isInviting, setIsInviting] = useState(false);

    const errorRef = useRef(error);
    const successRef = useRef(success);
    useEffect(() => {
        errorRef.current = error;
        successRef.current = success;
    }, [error, success]);

    const form = useForm<EmailFormValues>({
        resolver: zodResolver(emailSchema),
        defaultValues: {
            email: "",
        }
    });

    // Reset state when dialog opens/closes
    useEffect(() => {
        if (open) {
            form.reset({ email: "" });
            setVerificationState('idle');
            setVerificationResult(null);
            setSelectedRole('developer');
        }
    }, [open, form]);

    const handleVerify = async (data: EmailFormValues) => {
        setVerificationState('verifying');
        setVerificationResult(null);

        try {
            const result = await verifyUser({ email: data.email.toLowerCase() });

            if (result.error) {
                throw new Error(result.error);
            }

            setVerificationResult(result.data || null);
            setVerificationState('verified');
        } catch (err) {
            setVerificationState('error');
            errorRef.current({
                icon: AlertCircle,
                title: "Verification Failed",
                description: err instanceof Error ? err.message : "Could not verify email",
                duration: 4000,
            });
        }
    };

    const handleInvite = async () => {
        if (!verificationResult) return;

        setIsInviting(true);
        try {
            const inviteFunction = selectedRole === 'developer' ? inviteDeveloper : inviteManager;
            const result = await inviteFunction({ 
                email: form.getValues('email').toLowerCase(),
                role: selectedRole
            });

            if (result.error) {
                throw new Error(result.error);
            }

            successRef.current({
                icon: CheckCircle2,
                title: "Invitation Sent",
                description: result.data?.message || `Invitation sent to ${form.getValues('email')}`,
                duration: 4000,
            });

            onOpenChange(false);
            onSuccess?.();
        } catch (err) {
            errorRef.current({
                icon: AlertCircle,
                title: "Invitation Failed",
                description: err instanceof Error ? err.message : "Could not send invitation",
                duration: 4000,
            });
        } finally {
            setIsInviting(false);
        }
    };

    const handleReset = () => {
        form.reset({ email: "" });
        setVerificationState('idle');
        setVerificationResult(null);
    };

    // Can invite ONLY if: verified AND user doesn't exist at all
    // If user exists in any organization, they cannot be invited - must be removed first
    const canInvite = verificationState === 'verified' && 
        verificationResult && 
        !verificationResult.exists;

    const roleConfig = {
        developer: { 
            label: 'Developer', 
            icon: Code2,
            description: 'Can access assigned organizations and their data',
            color: 'text-blue-500 bg-blue-50 border-blue-200 dark:bg-blue-500/10 dark:border-blue-500/20'
        },
        manager: { 
            label: 'Manager', 
            icon: Shield,
            description: 'Can invite developers and manage organization access',
            color: 'text-purple-500 bg-purple-50 border-purple-200 dark:bg-purple-500/10 dark:border-purple-500/20'
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[480px] gap-0 p-0">
                <DialogHeader className="border-b bg-muted/20 px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                            <UserPlus className="h-4 w-4 text-primary" />
                        </div>
                        <div className="flex-1 space-y-1">
                            <DialogTitle className="text-base font-semibold">
                                Invite Team Member
                            </DialogTitle>
                            <DialogDescription className="text-xs text-muted-foreground">
                                Add a new developer or manager to the platform team
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleVerify)} className="px-6 py-5 space-y-5">
                        {/* Email Field with Verify Button */}
                        <FormField
                            control={form.control}
                            name="email"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <Mail className="h-4 w-4 text-muted-foreground" />
                                        Email Address
                                    </FormLabel>
                                    <div className="flex gap-2">
                                        <FormControl>
                                            <Input
                                                placeholder="user@example.com"
                                                type="email"
                                                autoComplete="email"
                                                disabled={verificationState === 'verified'}
                                                {...field}
                                            />
                                        </FormControl>
                                        {verificationState === 'verified' ? (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={handleReset}
                                                className="shrink-0"
                                            >
                                                Change
                                            </Button>
                                        ) : (
                                            <Button
                                                type="submit"
                                                variant="secondary"
                                                disabled={verificationState === 'verifying' || !field.value}
                                                className="shrink-0 gap-2"
                                            >
                                                {verificationState === 'verifying' ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Search className="h-4 w-4" />
                                                )}
                                                Verify
                                            </Button>
                                        )}
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Verification Result */}
                        {verificationState === 'verified' && verificationResult && (
                            <div className={cn(
                                "rounded-lg border p-4 space-y-3",
                                verificationResult.exists 
                                    ? "bg-destructive/10 border-destructive/20"
                                    : "bg-green-50 border-green-200 dark:bg-green-500/10 dark:border-green-500/20"
                            )}>
                                <div className="flex items-start gap-3">
                                    {verificationResult.exists ? (
                                        <XCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                                    ) : (
                                        <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className={cn(
                                            "text-sm font-medium",
                                            verificationResult.exists 
                                                ? "text-destructive"
                                                : "text-green-700 dark:text-green-300"
                                        )}>
                                            {verificationResult.exists 
                                                ? verificationResult.has_root_membership
                                                    ? "Already a Platform Team Member"
                                                    : "User Already Exists - Cannot Invite"
                                                : "New User - Ready to Invite"
                                            }
                                        </p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            {verificationResult.exists 
                                                ? verificationResult.has_root_membership
                                                    ? "This user is already part of the platform team."
                                                    : "This user exists in other organizations and must be removed from there before being invited to the platform team."
                                                : verificationResult.message
                                            }
                                        </p>
                                        
                                        {/* Show existing memberships if user exists */}
                                        {verificationResult.exists && verificationResult.memberships.length > 0 && (
                                            <div className="mt-3 space-y-1.5">
                                                <p className="text-xs font-medium text-muted-foreground">
                                                    Current memberships:
                                                </p>
                                                {verificationResult.memberships.map((membership, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className="flex items-center gap-2 text-xs bg-background/50 rounded px-2 py-1.5"
                                                    >
                                                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                                                        <span className="font-medium">{membership.organization_name}</span>
                                                        <span className="text-muted-foreground">({membership.role})</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Role Selection - Only show if can invite */}
                        {canInvite && (
                            <div className="space-y-3">
                                <Label className="text-sm font-medium">Select Role</Label>
                                <RadioGroup 
                                    value={selectedRole} 
                                    onValueChange={(value) => setSelectedRole(value as 'developer' | 'manager')}
                                    className="grid grid-cols-2 gap-3"
                                >
                                    {(['developer', 'manager'] as const).map((role) => {
                                        const config = roleConfig[role];
                                        const isSelected = selectedRole === role;
                                        return (
                                            <Label
                                                key={role}
                                                htmlFor={role}
                                                className={cn(
                                                    "flex flex-col items-center gap-2 rounded-lg border-2 p-4 cursor-pointer transition-all",
                                                    isSelected 
                                                        ? "border-primary bg-primary/5" 
                                                        : "border-border hover:border-primary/50 hover:bg-muted/50"
                                                )}
                                            >
                                                <RadioGroupItem 
                                                    value={role} 
                                                    id={role} 
                                                    className="sr-only"
                                                />
                                                <div className={cn(
                                                    "flex h-10 w-10 items-center justify-center rounded-lg border",
                                                    config.color
                                                )}>
                                                    <config.icon className="h-5 w-5" />
                                                </div>
                                                <div className="text-center">
                                                    <p className="font-medium text-sm">{config.label}</p>
                                                    <p className="text-xs text-muted-foreground mt-0.5">
                                                        {config.description}
                                                    </p>
                                                </div>
                                            </Label>
                                        );
                                    })}
                                </RadioGroup>
                            </div>
                        )}
                    </form>
                </Form>

                <DialogFooter className="border-t bg-muted/20 px-6 py-4">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={isInviting}
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="button"
                        onClick={handleInvite}
                        disabled={!canInvite || isInviting}
                        className="gap-2"
                    >
                        {isInviting ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Sending...
                            </>
                        ) : (
                            <>
                                <Mail className="h-4 w-4" />
                                Send Invitation
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
